import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatNumber } from "../utils/format.ts";
import DeltaChip from "./dashboard/DeltaChip.tsx";

interface KpiTileProps {
  label: string;
  /** A count (formatted for the locale) or a ready-made string ("4.2 MB"). */
  value: number | string;
  icon?: ReactNode;
  // Palette color of the icon badge (and of the number, when `emphasis`).
  color?: "primary" | "success" | "warning" | "error";
  emphasis?: boolean;
  caption?: ReactNode;
  // Change since a reference point, as a green/red chip next to the number.
  delta?: number;
  deltaLabel?: string;
  // Meter, sparkline or links under the number.
  children?: ReactNode;
  // Makes the whole tile a link; leave out when `children` hold links.
  to?: string;
  // Smaller number, for a row of many tiles.
  dense?: boolean;
}

/** One headline number: an icon badge, the count, a caption and an optional visual. */
export default function KpiTile({
  label,
  value,
  icon,
  color = "primary",
  emphasis,
  caption,
  delta,
  deltaLabel,
  children,
  to,
  dense = false,
}: KpiTileProps) {
  const content = (
    <Stack spacing={dense ? 1 : 1.5} sx={{ p: dense ? 2 : 2.5, height: "100%" }}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: `${color}.main`,
              bgcolor: `rgba(var(--mui-palette-${color}-mainChannel) / 0.12)`,
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="subtitle1" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: dense ? "1.5rem" : "2.25rem",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              color: emphasis ? `${color}.main` : "text.primary",
            }}
          >
            {typeof value === "number" ? formatNumber(value) : value}
          </Typography>
          {delta !== undefined && <DeltaChip value={delta} label={deltaLabel} />}
        </Stack>
        {caption && (
          <Typography variant="body2" component="div" sx={{ color: "text.secondary", mt: 0.5 }}>
            {caption}
          </Typography>
        )}
      </Box>
      {children && <Box sx={{ mt: "auto" }}>{children}</Box>}
    </Stack>
  );
  return (
    <Card sx={{ height: "100%" }}>
      {to ? (
        <CardActionArea component={Link} to={to} sx={{ height: "100%", alignItems: "stretch" }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
